import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import {
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Tooltip,
    Legend
} from "recharts";

import { getAnalyticsWidget } from "../../../utils/Requests";
import { AnalyticsFilters, WidgetTheme } from "../../../types/analytics";
import { WidgetCard, PopPeriodBanner } from "../../../components/analytics";
import Branding from "../../../config/Branding";

// Recharts TypeScript workaround — same pattern used in FactorsRadar.tsx
const RESP: any = ResponsiveContainer as any;
const RC: any = RadarChart as any;
const PG: any = PolarGrid as any;
const PAA: any = PolarAngleAxis as any;
const PRA: any = PolarRadiusAxis as any;
const RD: any = Radar as any;
const TT: any = Tooltip as any;
const LG: any = Legend as any;

// ------------------------------------------------------------------ types

type ContentGenreRadarWidgetProps = {
    customerConfig: CustomerConfig;
    appliedFilters: AnalyticsFilters;
    theme: WidgetTheme;
};

// API rows use snake_case keys — use Record<string, any> to satisfy the camelcase rule
type GenreRadarRow = Record<string, any>;

type MetricConfig = {
    key: string;
    label: string;
    /** Field name for the current-period normalised value (0–1). */
    normKey: string;
    /** Field name for the period-over-period percentage change of the raw metric. */
    popKey: string;
    /** When true, a positive PoP is bad (e.g. bounce rate increasing). */
    invertPoP?: boolean;
};

type MetricDataPoint = {
    genre: string;
    current: number;
    previous: number;
    pop: number;
};

// ------------------------------------------------------------------ constants

const METRICS: MetricConfig[] = [
    {
        key: "plays_per_session",
        label: "Plays / Session",
        normKey: "plays_per_session_norm",
        popKey: "plays_per_session_pop_pct"
    },
    {
        key: "bounce_rate",
        label: "Bounce Rate",
        normKey: "bounce_rate_norm",
        popKey: "bounce_rate_pop_pct",
        invertPoP: true
    },
    {
        key: "avg_progress",
        label: "Avg Progress",
        normKey: "avg_progress_norm",
        popKey: "avg_progress_pop_pct"
    },
    {
        key: "plays_per_user",
        label: "Plays / User",
        normKey: "plays_per_user_norm",
        popKey: "plays_per_user_pop_pct"
    },
    {
        key: "pct_users_recs",
        label: "% Users Using Recs",
        normKey: "pct_users_recs_norm",
        popKey: "pct_users_recs_pop_pct"
    },
    {
        key: "pct_plays_from_recs",
        label: "Plays Share from Recs",
        normKey: "pct_plays_from_recs_norm",
        popKey: "pct_plays_from_recs_pop_pct"
    }
];

const CURRENT_COLOR = Branding.primary;
const PREVIOUS_COLOR = Branding.twentyfouriLime;

// ------------------------------------------------------------------ styled

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
`;

const MetricLabel = styled.label`
    font-size: 13px;
    color: ${Branding.white};
    white-space: nowrap;
    margin: 0;
`;

// ------------------------------------------------------------------ helpers

const renderAngleTick = (props: any) => {
    const { payload, x, y, textAnchor } = props;
    const label = String(payload?.value ?? "");
    const display = label.length > 12 ? `${label.slice(0, 11)}\u2026` : label;
    return (
        <text
            x={x}
            y={y}
            textAnchor={textAnchor}
            fill={Branding.white}
            fontSize={10}
            style={{ pointerEvents: "none" }}
        >
            {display}
        </text>
    );
};

// ------------------------------------------------------------------ tooltip

type CustomTooltipProps = {
    active?: boolean;
    payload?: any[];
    metric: MetricConfig;
    data: MetricDataPoint[];
};

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, metric, data }) => {
    if (!active || !payload || payload.length === 0) return null;

    const genreName: string = payload[0]?.payload?.genre ?? "";
    const row = data.find(d => d.genre === genreName);
    if (!row) return null;

    const pop = row.pop;
    const isPositive = pop >= 0;
    const popIsGood = metric.invertPoP ? !isPositive : isPositive;
    const popColor = popIsGood ? CURRENT_COLOR : "#ff3a10";
    const popSign = isPositive ? "+" : "";

    return (
        <div
            style={{
                background: "rgba(0,0,0,0.88)",
                border: "1px solid #444",
                borderRadius: 6,
                padding: "8px 12px",
                fontSize: 12,
                minWidth: 150
            }}
        >
            <div style={{ color: Branding.white, fontWeight: 600, marginBottom: 4 }}>{genreName}</div>
            <div style={{ color: CURRENT_COLOR }}>Current:&nbsp;{row.current.toFixed(3)}</div>
            <div style={{ color: PREVIOUS_COLOR }}>Previous:&nbsp;{row.previous.toFixed(3)}</div>
            <div style={{ color: popColor, marginTop: 2 }}>
                PoP:&nbsp;{popSign}{pop.toFixed(1)}%
            </div>
        </div>
    );
};

// ------------------------------------------------------------------ component

const ContentGenreRadarWidget: React.FC<ContentGenreRadarWidgetProps> = ({
    customerConfig,
    appliedFilters,
    theme
}) => {
    const [data, setData] = useState<GenreRadarRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedMetricKey, setSelectedMetricKey] = useState(METRICS[0].key);
    const requestIdRef = useRef(0);

    const fetchData = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);
        try {
            const response = await getAnalyticsWidget(
                customerConfig,
                "content_genre_radar",
                appliedFilters.startDate,
                appliedFilters.endDate,
                appliedFilters.attributionId,
                appliedFilters.brandName,
                appliedFilters.devicePlatform
            );
            if (requestId !== requestIdRef.current) return;
            const rows = Array.isArray((response as any)?.result)
                ? (response as any).result
                : [];
            setData(rows as GenreRadarRow[]);
        } catch {
            if (requestId !== requestIdRef.current) return;
            setError("Failed to load genre radar data.");
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [
        customerConfig,
        appliedFilters.startDate,
        appliedFilters.endDate,
        appliedFilters.attributionId,
        appliedFilters.brandName,
        appliedFilters.devicePlatform
    ]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const selectedMetric = METRICS.find(m => m.key === selectedMetricKey) ?? METRICS[0];

    const metricData: MetricDataPoint[] = data.map(row => {
        const currentNorm = (row[selectedMetric.normKey] as number) ?? 0;
        const popPct = (row[selectedMetric.popKey] as number) ?? 0;
        const divisor = 1 + popPct / 100;
        const previousNorm = divisor !== 0 ? currentNorm / divisor : currentNorm;
        return {
            genre: row.genre,
            current: Math.max(0, Math.min(1, currentNorm)),
            previous: Math.max(0, Math.min(1, previousNorm)),
            pop: popPct
        };
    });

    return (
        <WidgetCard
            title="Genre Radar"
            theme={theme}
            isLoading={isLoading}
            error={error}
            isEmpty={!isLoading && !error && data.length === 0}
            emptyMessage="No genre radar data available for the selected date range."
        >
            <PopPeriodBanner
                startDate={appliedFilters.startDate}
                endDate={appliedFilters.endDate}
            />
            <Controls>
                <MetricLabel htmlFor="genre-radar-metric-select">Metric</MetricLabel>
                <Form.Control
                    as="select"
                    id="genre-radar-metric-select"
                    size="sm"
                    value={selectedMetricKey}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedMetricKey(e.target.value)}
                    style={{ maxWidth: 220 }}
                >
                    {METRICS.map(m => (
                        <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                </Form.Control>
            </Controls>
            <RESP width="100%" height={480}>
                <RC
                    data={metricData}
                    cx="50%"
                    cy="50%"
                    outerRadius="72%"
                    margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                >
                    <PG stroke={theme.border} />
                    <PAA
                        dataKey="genre"
                        tick={renderAngleTick}
                        tickLine={false}
                        stroke={theme.border}
                    />
                    <PRA domain={[0, 1]} tick={false} axisLine={false} />
                    <TT
                        content={(props: any) => (
                            <CustomTooltip
                                active={props.active}
                                payload={props.payload}
                                metric={selectedMetric}
                                data={metricData}
                            />
                        )}
                    />
                    <LG
                        iconType="circle"
                        iconSize={9}
                        wrapperStyle={{ fontSize: 12, paddingTop: 6 }}
                    />
                    <RD
                        name="Previous"
                        dataKey="previous"
                        stroke={PREVIOUS_COLOR}
                        fill={PREVIOUS_COLOR}
                        fillOpacity={0.2}
                        strokeWidth={2}
                        dot={false}
                        strokeDasharray="4 2"
                    />
                    <RD
                        name="Current"
                        dataKey="current"
                        stroke={CURRENT_COLOR}
                        fill={CURRENT_COLOR}
                        fillOpacity={0.35}
                        strokeWidth={2}
                        dot={false}
                    />
                </RC>
            </RESP>
        </WidgetCard>
    );
};

export default ContentGenreRadarWidget;
